/**
 * Runtime 监控 API 服务
 * 封装运行时监控接口
 * 基于 agent-runtime-api.md v1.1 协议文档
 * 
 * 📌 架构变更（2024-12-25）：
 * 前端统一通过 Agent Admin (8001) 调用运行时 API
 * Agent Admin 会自动转发 /runtime/* 请求到 Agent Runtime (8002)
 */

import { ApiClient, apiClients } from '@/shared/services/api'

// ==================== 类型定义 ====================

/**
 * 健康状态枚举
 */
export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown'

/**
 * 测试状态枚举
 */
export type TestStatus = 'passed' | 'failed'

/**
 * 测试类型枚举
 */
export type TestType = 'manual' | 'scheduled' | 'api'

/**
 * 项目运行时状态（列表）
 */
export interface ProjectRuntimeStatus {
  project_id: string
  project_name: string
  active_sessions: number
  health_status: HealthStatus
  has_published_flow: boolean
  last_test_at: string | null
  last_test_status: TestStatus | null
}

/**
 * 测试统计
 */
export interface TestStatistics {
  total: number
  passed: number
  failed: number
  pass_rate: number
  avg_duration_ms: number
}

/**
 * 测试记录
 */
export interface TestRecord {
  test_id: string
  test_type: TestType
  status: TestStatus
  started_at: string
  completed_at: string
  duration_ms: number
  input_message: string
  output_response: string
  nodes_executed?: string[]
  error_message?: string | null
  triggered_by?: string
}

/**
 * 项目运行时状态详情
 */
export interface ProjectRuntimeDetail extends ProjectRuntimeStatus {
  test_statistics: TestStatistics
  recent_tests: TestRecord[]
}

/**
 * 测试执行请求
 */
export interface TestExecuteRequest {
  message: string
  test_type?: TestType
  device_id?: string  // 用于 IoT 设备控制场景
}

/**
 * 测试执行响应
 */
export interface TestExecuteResponse {
  test_id: string
  project_id: string
  status: TestStatus
  input_message: string
  output_response: string
  nodes_executed: string[]
  first_token_ms: number | null  // 🆕 首包耗时（TTFB），单位毫秒
  duration_ms: number             // 完整耗时，单位毫秒
  error_message: string | null
}

// ==================== 兼容旧类型（向后兼容） ====================

/**
 * @deprecated 请使用 ProjectRuntimeStatus
 */
export interface ProjectRuntimeMetrics {
  project_id: string
  project_name?: string
  active_session_count: number
  health_score: number
  error_rate?: number
  avg_response_time?: number
  success_rate?: number
  last_updated_at?: string
}

/**
 * @deprecated 请使用 TestExecuteRequest
 */
export interface TestCaseRequest {
  message: string
  flow?: unknown
  test_mode?: boolean
}

/**
 * @deprecated 请使用 TestExecuteResponse
 */
export interface TestCaseResponse {
  success: boolean
  response_time_ms: number
  error?: string
  response?: unknown
}

// ==================== API 服务 ====================

/**
 * Runtime 监控 API 服务
 */
export const runtimeApi = {
  // ==================== 项目状态接口 ====================

  /**
   * 获取所有项目的运行时状态
   * GET /api/v1/runtime/projects
   */
  async getProjects(params?: { limit?: number; offset?: number }): Promise<{
    data: ProjectRuntimeStatus[]
    meta: { total: number; limit: number; offset: number }
  }> {
    // 后端返回格式: { success, data: { items: [...], total, limit, offset }, meta }
    const response = await ApiClient.getWithClient<{
      items: ProjectRuntimeStatus[]
      total: number
      limit: number
      offset: number
    }>(
      apiClients.agentAdmin,
      '/runtime/projects',
      { params }
    )
    console.log('📊 [RuntimeApi] getProjects 响应:', JSON.stringify(response, null, 2))
    
    // 兼容处理：后端返回 data.items 或直接返回数组
    const items = Array.isArray(response.data) 
      ? response.data 
      : (response.data?.items || [])
    
    return {
      data: items,
      meta: {
        total: response.data?.total ?? items.length,
        limit: response.data?.limit ?? params?.limit ?? 50,
        offset: response.data?.offset ?? params?.offset ?? 0
      }
    }
  },

  /**
   * 获取单个项目的运行时状态详情
   * GET /api/v1/runtime/projects/{project_id}
   */
  async getProjectDetail(projectId: string): Promise<ProjectRuntimeDetail> {
    const response = await ApiClient.getWithClient<ProjectRuntimeDetail>(
      apiClients.agentAdmin,
      `/runtime/projects/${projectId}`
    )
    console.log('📊 [RuntimeApi] getProjectDetail 响应:', JSON.stringify(response, null, 2))
    return response.data
  },

  // ==================== 测试执行接口 ====================

  /**
   * 执行项目测试
   * POST /api/v1/runtime/projects/{project_id}/test
   */
  async executeTest(projectId: string, request: TestExecuteRequest): Promise<TestExecuteResponse> {
    const response = await ApiClient.postWithClient<TestExecuteResponse>(
      apiClients.agentAdmin,
      `/runtime/projects/${projectId}/test`,
      request
    )
    console.log('📊 [RuntimeApi] executeTest 响应:', JSON.stringify(response, null, 2))
    return response.data
  },

  /**
   * 获取测试历史
   * GET /api/v1/runtime/projects/{project_id}/tests
   */
  async getTestHistory(projectId: string, params?: { limit?: number; offset?: number }): Promise<{
    data: TestRecord[]
    meta: { total: number; limit: number; offset: number }
  }> {
    const response = await ApiClient.getWithClient<TestRecord[]>(
      apiClients.agentAdmin,
      `/runtime/projects/${projectId}/tests`,
      { params }
    )
    console.log('📊 [RuntimeApi] getTestHistory 响应:', JSON.stringify(response, null, 2))
    // 后端可能在 meta 中返回分页信息，使用类型断言处理
    const paginationMeta = (response as any).meta as { total?: number; limit?: number; offset?: number } | undefined
    return {
      data: response.data,
      meta: { 
        total: paginationMeta?.total ?? response.data?.length ?? 0, 
        limit: paginationMeta?.limit ?? 20, 
        offset: paginationMeta?.offset ?? 0 
      }
    }
  },

  /**
   * 获取测试统计
   * GET /api/v1/runtime/projects/{project_id}/statistics
   */
  async getStatistics(projectId: string, days?: number): Promise<TestStatistics> {
    const response = await ApiClient.getWithClient<TestStatistics>(
      apiClients.agentAdmin,
      `/runtime/projects/${projectId}/statistics`,
      { params: { days } }
    )
    console.log('📊 [RuntimeApi] getStatistics 响应:', JSON.stringify(response, null, 2))
    return response.data
  },

  // ==================== 兼容旧接口（向后兼容） ====================

  /**
   * @deprecated 请使用 getProjects
   */
  async getProjectsLegacy(): Promise<{ projects: ProjectRuntimeMetrics[]; total: number }> {
    const response = await this.getProjects()
    // 转换新格式到旧格式
    const projects: ProjectRuntimeMetrics[] = response.data.map(p => ({
      project_id: p.project_id,
      project_name: p.project_name,
      active_session_count: p.active_sessions,
      health_score: p.health_status === 'healthy' ? 100 : 
                    p.health_status === 'degraded' ? 60 : 
                    p.health_status === 'unhealthy' ? 20 : 0,
    }))
    return { projects, total: response.meta.total }
  },

  /**
   * @deprecated 请使用 getProjectDetail
   */
  async getProject(projectId: string): Promise<ProjectRuntimeMetrics> {
    const detail = await this.getProjectDetail(projectId)
    return {
      project_id: detail.project_id,
      project_name: detail.project_name,
      active_session_count: detail.active_sessions,
      health_score: detail.health_status === 'healthy' ? 100 : 
                    detail.health_status === 'degraded' ? 60 : 
                    detail.health_status === 'unhealthy' ? 20 : 0,
      avg_response_time: detail.test_statistics?.avg_duration_ms,
      success_rate: detail.test_statistics?.pass_rate ? detail.test_statistics.pass_rate / 100 : undefined,
    }
  },

  /**
   * @deprecated 请使用 executeTest
   */
  async runTest(projectId: string, testCase: TestCaseRequest): Promise<TestCaseResponse> {
    try {
      const result = await this.executeTest(projectId, {
        message: testCase.message,
        test_type: 'manual'
      })
      return {
        success: result.status === 'passed',
        response_time_ms: result.duration_ms,
        error: result.error_message || undefined,
        response: result.output_response
      }
    } catch (err) {
      return {
        success: false,
        response_time_ms: 0,
        error: err instanceof Error ? err.message : '测试执行失败'
      }
    }
  },
}
